/**
 * Tiny WebAudio sound layer for games.
 *
 * - No asset files: every sound is synthesized on demand, so there's nothing
 *   to download and the bundle stays tiny.
 * - Mute preference is persisted in localStorage and shared across games via
 *   a module-level store + `useSound` hook.
 * - All playback goes through `play()` which respects the mute flag and
 *   lazily creates the AudioContext on first user interaction (autoplay
 *   policy friendly).
 */
import { useEffect, useState } from "react";

const STORAGE_KEY = "elementpad:sound-muted";

let ctx: AudioContext | null = null;
let masterGain: GainNode | null = null;
let unlockInstalled = false;

function getCtx(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (ctx) return ctx;
  const Ctor =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
    masterGain = ctx.createGain();
    masterGain.gain.value = 0.18; // keep it polite
    masterGain.connect(ctx.destination);
  } catch {
    return null;
  }
  return ctx;
}

/**
 * Mobile browsers (especially iOS Safari) require the AudioContext to be
 * created and resumed from inside a user gesture. They also keep the context
 * in `suspended` state until a silent buffer is played within that gesture.
 *
 * This installer wires a one-shot listener on the first pointer/touch/key
 * event after load. It creates the context, resumes it, and plays a 1-sample
 * silent buffer so subsequent `play()` calls (even from timers) work.
 *
 * It also re-resumes the context whenever the tab becomes visible again,
 * since iOS suspends WebAudio on backgrounding/lock.
 */
export function installAudioUnlock(): void {
  if (typeof window === "undefined" || unlockInstalled) return;
  unlockInstalled = true;

  const unlock = () => {
    const c = getCtx();
    if (!c) return;
    if (c.state === "suspended") c.resume().catch(() => undefined);
    try {
      // Play a 1-sample silent buffer to fully unlock iOS WebAudio.
      const buf = c.createBuffer(1, 1, 22050);
      const src = c.createBufferSource();
      src.buffer = buf;
      src.connect(c.destination);
      src.start(0);
    } catch {
      /* ignore */
    }
  };

  const onGesture = () => {
    unlock();
    window.removeEventListener("pointerdown", onGesture);
    window.removeEventListener("touchstart", onGesture);
    window.removeEventListener("keydown", onGesture);
  };
  window.addEventListener("pointerdown", onGesture, { passive: true });
  window.addEventListener("touchstart", onGesture, { passive: true });
  window.addEventListener("keydown", onGesture);

  // Re-resume on tab return (iOS suspends on background/lock).
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && ctx && ctx.state === "suspended") {
      ctx.resume().catch(() => undefined);
    }
  });
}

// ----- mute store (module-level pub/sub so all components stay in sync) -----

let muted: boolean = readInitialMuted();
const listeners = new Set<(m: boolean) => void>();

function readInitialMuted(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function isMuted(): boolean {
  return muted;
}

export function setMuted(next: boolean): void {
  muted = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
  } catch {
    /* ignore */
  }
  listeners.forEach((l) => l(next));
}

/** React hook: `[muted, toggle]`. */
export function useSound(): [boolean, () => void] {
  const [m, setM] = useState<boolean>(muted);
  useEffect(() => {
    const fn = (next: boolean) => setM(next);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);
  return [m, () => setMuted(!muted)];
}

// ----- low-level tone helper -----

type ToneOpts = {
  freq: number;
  duration: number; // seconds
  type?: OscillatorType;
  gain?: number; // 0..1, multiplied with master
  attack?: number;
  release?: number;
};

function tone({
  freq,
  duration,
  type = "sine",
  gain = 1,
  attack = 0.005,
  release = 0.05,
}: ToneOpts) {
  const c = getCtx();
  if (!c || !masterGain) return;
  // Resume on demand (Safari/iOS autoplay).
  if (c.state === "suspended") c.resume().catch(() => undefined);
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  const now = c.currentTime;
  g.gain.setValueAtTime(0, now);
  g.gain.linearRampToValueAtTime(gain, now + attack);
  g.gain.linearRampToValueAtTime(0, now + duration + release);
  osc.connect(g);
  g.connect(masterGain);
  osc.start(now);
  osc.stop(now + duration + release + 0.02);
}

// ----- named effects -----

export type SfxName =
  | "tick"
  | "tickFast"
  | "expire"
  | "correct"
  | "wrong"
  | "win"
  | "lose"
  | "click";

export function play(name: SfxName): void {
  if (muted) return;
  switch (name) {
    case "tick":
      tone({ freq: 880, duration: 0.03, type: "square", gain: 0.25 });
      break;
    case "tickFast":
      tone({ freq: 1320, duration: 0.04, type: "square", gain: 0.4 });
      break;
    case "expire":
      tone({ freq: 220, duration: 0.18, type: "sawtooth", gain: 0.5 });
      window.setTimeout(
        () => tone({ freq: 165, duration: 0.22, type: "sawtooth", gain: 0.5 }),
        90,
      );
      break;
    case "correct":
      tone({ freq: 660, duration: 0.08, type: "triangle", gain: 0.5 });
      window.setTimeout(
        () => tone({ freq: 990, duration: 0.12, type: "triangle", gain: 0.5 }),
        70,
      );
      break;
    case "wrong":
      tone({ freq: 200, duration: 0.16, type: "square", gain: 0.45 });
      break;
    case "win":
      [523, 659, 784, 1046].forEach((f, i) =>
        window.setTimeout(
          () => tone({ freq: f, duration: 0.14, type: "triangle", gain: 0.5 }),
          i * 110,
        ),
      );
      break;
    case "lose":
      [400, 330, 247].forEach((f, i) =>
        window.setTimeout(
          () => tone({ freq: f, duration: 0.18, type: "sawtooth", gain: 0.5 }),
          i * 130,
        ),
      );
      break;
    case "click":
      tone({ freq: 600, duration: 0.02, type: "square", gain: 0.2 });
      break;
  }
}